/**
 * Two Sense — per-route document head + structured data.
 *
 * Hand-rolled rather than react-helmet: Helmet's maintained fork
 * (react-helmet-async) still declares a React ≤18 peer range, and this project
 * is on React 19. The surface we actually need is small — title, description,
 * canonical, Open Graph, Twitter, JSON-LD — so a ~60-line hook avoids a
 * dependency that would need `--legacy-peer-deps` to install.
 *
 * IMPORTANT — what this hook can and cannot do:
 * These tags are written by JavaScript AFTER mount. Google renders JS and will
 * see them; GPTBot, PerplexityBot and ClaudeBot do NOT execute JS and will not.
 * The same values are therefore also baked into static HTML at build time by
 * `scripts/generate-static-seo.js`, which is what non-JS crawlers actually
 * read. Both paths derive from ROUTE_SEO below, so they cannot drift apart.
 */

import { useEffect } from 'react';
import { SITE_URL, BUSINESS, OG_IMAGE } from './constant';

/* ── Per-route metadata ──
   Titles stay under ~60 characters and descriptions under ~155 so neither is
   truncated in search results. Each description leads with what the page is,
   then the location — local intent ("hair salon johor bahru") is the realistic
   query this site competes for. */
export const ROUTE_SEO = {
    '/': {
        title: 'Two Sense Hair & Beauty — Hair & Skin Studio, Johor Bahru',
        description:
            'A considered hair and skin studio in Bandar Jaya Putra, Johor Bahru. Colour, cuts, nails and facials under one roof — by appointment.',
    },
    '/services': {
        title: 'Service Menu — Two Sense Hair & Beauty, Johor Bahru',
        description:
            'Browse the full Two Sense service menu: hair cutting and colour, corrective treatments, nails, lashes and facials, with prices and durations.',
    },
    '/appointment': {
        title: 'Book an Appointment — Two Sense Hair & Beauty',
        description:
            'Reserve your session at Two Sense Hair & Beauty, Johor Bahru. Choose a service, pick a time, and we confirm personally by WhatsApp.',
    },
};

const absolute = (path) => `${SITE_URL}${path === '/' ? '/' : path}`;

/* Upsert a <meta> by name or property, so repeated route changes update the
   existing tag instead of appending duplicates. */
const setMeta = (key, value, attr = 'name') => {
    if (!value) return;
    let el = document.head.querySelector(`meta[${attr}="${key}"]`);
    if (!el) {
        el = document.createElement('meta');
        el.setAttribute(attr, key);
        document.head.appendChild(el);
    }
    el.setAttribute('content', value);
};

const setLink = (rel, href) => {
    let el = document.head.querySelector(`link[rel="${rel}"]`);
    if (!el) {
        el = document.createElement('link');
        el.setAttribute('rel', rel);
        document.head.appendChild(el);
    }
    el.setAttribute('href', href);
};

/**
 * Apply the head tags for a route. Pass the pathname; unknown paths fall back
 * to the home entry rather than leaving a stale title from the previous route.
 */
export const useSeo = (pathname) => {
    useEffect(() => {
        const meta = ROUTE_SEO[pathname] || ROUTE_SEO['/'];
        const url = absolute(pathname);
        const image = `${SITE_URL}${OG_IMAGE}`;

        document.title = meta.title;
        setMeta('description', meta.description);
        setLink('canonical', url);

        setMeta('og:title', meta.title, 'property');
        setMeta('og:description', meta.description, 'property');
        setMeta('og:url', url, 'property');
        setMeta('og:image', image, 'property');
        setMeta('og:type', 'website', 'property');
        setMeta('og:site_name', BUSINESS.name, 'property');
        setMeta('og:locale', 'en_MY', 'property');

        setMeta('twitter:card', 'summary_large_image');
        setMeta('twitter:title', meta.title);
        setMeta('twitter:description', meta.description);
        setMeta('twitter:image', image);
    }, [pathname]);
};

/* ── Structured data ──
   Emitted as JSON-LD, which is the format Google documents as preferred and
   the one answer engines parse most reliably. */

/** The studio itself. Drives the local pack, the knowledge panel and the
 *  "opening hours / phone / address" answers that AI engines quote. */
export const localBusinessSchema = () => {
    const a = BUSINESS.address;
    const schema = {
        '@context': 'https://schema.org',
        '@type': BUSINESS.schemaType,
        '@id': `${SITE_URL}/#business`,
        name: BUSINESS.name,
        description: BUSINESS.description,
        url: `${SITE_URL}/`,
        telephone: BUSINESS.telephone,
        priceRange: BUSINESS.priceRange,
        currenciesAccepted: BUSINESS.currency,
        image: `${SITE_URL}${OG_IMAGE}`,
        logo: `${SITE_URL}/images/logo.png`,
        address: {
            '@type': 'PostalAddress',
            streetAddress: a.street,
            addressLocality: a.locality,
            addressRegion: a.region,
            postalCode: a.postalCode,
            addressCountry: a.country,
        },
        openingHoursSpecification: BUSINESS.hours.map((h) => ({
            '@type': 'OpeningHoursSpecification',
            dayOfWeek: h.days,
            opens: h.opens,
            closes: h.closes,
        })),
        availableLanguage: BUSINESS.languages,
        /* The studio takes bookings by WhatsApp and through the site, so both
           are declared as reservation actions. */
        potentialAction: {
            '@type': 'ReserveAction',
            target: {
                '@type': 'EntryPoint',
                urlTemplate: `${SITE_URL}/appointment`,
                inLanguage: 'en',
                actionPlatform: [
                    'https://schema.org/DesktopWebPlatform',
                    'https://schema.org/MobileWebPlatform',
                ],
            },
            result: { '@type': 'Reservation', name: 'Salon appointment' },
        },
    };
    // Only emit sameAs once real profile URLs exist — an empty array is noise.
    if (BUSINESS.sameAs.length) schema.sameAs = BUSINESS.sameAs;
    return schema;
};

/** Trail for the current route. Helps search engines show the site hierarchy
 *  instead of a bare URL under the title. */
export const breadcrumbSchema = (pathname) => {
    const crumbs = [{ name: 'Home', path: '/' }];
    if (pathname === '/services') crumbs.push({ name: 'Services', path: '/services' });
    if (pathname === '/appointment') crumbs.push({ name: 'Book an Appointment', path: '/appointment' });
    return {
        '@context': 'https://schema.org',
        '@type': 'BreadcrumbList',
        itemListElement: crumbs.map((c, i) => ({
            '@type': 'ListItem',
            position: i + 1,
            name: c.name,
            item: absolute(c.path),
        })),
    };
};

/**
 * The live service menu, as an OfferCatalog grouped by category.
 *
 * Built from data fetched at runtime, so it is only visible to crawlers that
 * execute JavaScript (i.e. Google). Non-JS answer engines get the catalogue
 * summary from llms.txt instead. Capped because a 252-entry catalogue produces
 * a ~90 KB script tag for no additional ranking benefit.
 */
export const serviceCatalogSchema = (services, categories, { maxPerCategory = 12 } = {}) => {
    if (!Array.isArray(services) || !services.length) return null;
    const byCategory = new Map();
    for (const s of services) {
        const cat = s?.category?.name;
        if (!cat) continue;
        if (!byCategory.has(cat)) byCategory.set(cat, []);
        const list = byCategory.get(cat);
        if (list.length < maxPerCategory) list.push(s);
    }
    return {
        '@context': 'https://schema.org',
        '@type': 'OfferCatalog',
        name: 'Two Sense service menu',
        url: `${SITE_URL}/services`,
        provider: { '@id': `${SITE_URL}/#business` },
        numberOfItems: services.length,
        itemListElement: Array.from(byCategory.entries()).map(([name, items], i) => ({
            '@type': 'OfferCatalog',
            position: i + 1,
            name,
            itemListElement: items.map((s) => ({
                '@type': 'Offer',
                itemOffered: {
                    '@type': 'Service',
                    name: s.name,
                    ...(s.description ? { description: s.description } : {}),
                    serviceType: name,
                    /* No `image` here on purpose. The photos now live only in
                       labeau, which serves them as presigned S3 links that
                       expire in ~1h — unusable in structured data a crawler
                       stores and revisits, and there is no longer a stable
                       local copy to point at. Schema.org lists image as
                       recommended, not required, so it is omitted rather than
                       filled with a link that dies within the hour. */
                    provider: { '@id': `${SITE_URL}/#business` },
                },
                price: String(s.shop_price ?? s.price ?? ''),
                priceCurrency: BUSINESS.currency,
                availability: 'https://schema.org/InStock',
            })),
        })),
    };
};

/**
 * Mount a JSON-LD <script> for the lifetime of the component. `id` keeps
 * repeated renders from stacking duplicate blocks.
 */
export const useJsonLd = (id, schema) => {
    useEffect(() => {
        if (!schema) return undefined;
        let el = document.getElementById(id);
        if (!el) {
            el = document.createElement('script');
            el.type = 'application/ld+json';
            el.id = id;
            document.head.appendChild(el);
        }
        el.textContent = JSON.stringify(schema);
        return () => {
            document.getElementById(id)?.remove();
        };
    }, [id, schema]);
};
